import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FALLBACK_MENU_KEY } from '../../constants/clientConfigFallback';

export type SupportedLanguageCode = 'en' | 'fr';

export type DictionaryLabels = Record<string, string>;

export type DomainDictionary = Record<SupportedLanguageCode, DictionaryLabels>;

export type DictionaryState = Record<string, DomainDictionary>;

/** Labels for the reduced `anamnesis` menuKey (local / stage / prod buckets share the same wording). */
const ANAMNESIS_EN_LABELS: DictionaryLabels = {
  "sidebar-menu-dashboard": "Dashboard",
  "sidebar-menu-settings": "Settings",
  "sidebar-menu-settings-profile": "Profile",
  "sidebar-menu-settings-security": "Security",
  "sidebar-menu-support": "Support",
  "sidebar-menu-support-my-tickets": "My Tickets",
  "sidebar-menu-support-email": "Email",
  "sidebar-menu-anamnesis": "Anamnesis",
  "sidebar-menu-anamnesis-settings": "Settings",
  "sidebar-menu-anamnesis-knowledge-graphs": "Knowledge Graphs",
  "sidebar-menu-anamnesis-ai-prompts": "AI Prompts",
  "modules-anamnesis-dashboard-main-title": "Anamnesis Dashboard",
};

const buildAnamnesisDomainDictionary = (): DomainDictionary => ({
  en: { ...ANAMNESIS_EN_LABELS },
  fr: {},
});

/** Labels for the reduced `infralytiqs` menuKey (local / stage / prod buckets share the same wording). */
const INFRALYTIQS_EN_LABELS: DictionaryLabels = {
  "sidebar-menu-dashboard": "Dashboard",
  "sidebar-menu-settings": "Settings",
  "sidebar-menu-settings-profile": "Profile",
  "sidebar-menu-settings-security": "Security",
  "sidebar-menu-support": "Support",
  "sidebar-menu-support-my-tickets": "My Tickets",
  "sidebar-menu-support-email": "Email",
  "sidebar-menu-infralytiqs": "Infralytiqs",
  "sidebar-menu-infralytiqs-settings": "Settings",
  "sidebar-menu-infralytiqs-reports": "Reports",
  "sidebar-menu-infralytiqs-logs": "Logs",
  "sidebar-menu-infralytiqs-audit-logs": "Audit Logs",
  "modules-infralytiqs-dashboard-main-title": "Infralytiqs Dashboard",
};

const buildInfralytiqsDomainDictionary = (): DomainDictionary => ({
  en: { ...INFRALYTIQS_EN_LABELS },
  fr: {},
});

const initialState: DictionaryState = {
  "my-local.campaignking.ai": {
    en: {
      "sidebar-menu-dashboard": "Dashboard",
      "sidebar-menu-settings": "Settings",
      "sidebar-menu-settings-profile": "Profile",
      "sidebar-menu-settings-security": "Security",
      "sidebar-menu-settings-notifications": "Notifications",
      "sidebar-menu-settings-users": "Users",
      "sidebar-menu-settings-companies": "Companies",
      "sidebar-menu-settings-whatsapp": "WhatsApp",
      "sidebar-menu-settings-whatsappbusiness": "WhatsApp Business",
      "sidebar-menu-settings-telegram": "Telegram",
      "sidebar-menu-settings-sms": "GSM Text",
      "sidebar-menu-settings-email": "Email",
      "sidebar-menu-settings-bot-manager": "BOT Manager",
      "sidebar-menu-settings-openai": "OpenAI",
      "sidebar-menu-settings-youtube": "YouTube",
      "sidebar-menu-settings-instagram": "Instagram",
      "sidebar-menu-settings-tiktok": "TikTok",
      "sidebar-menu-settings-signal": "Signal",
      "sidebar-menu-instant-updates": "Campaigns",
      "sidebar-menu-instant-updates-data-sets": "Data Sets",
      "sidebar-menu-instant-updates-templates": "Templates",
      "sidebar-menu-instant-updates-runner": "Runner",
      "sidebar-menu-instant-updates-activities": "Activities",
      "sidebar-menu-administration-domains": "Domains",
      "sidebar-menu-administration-support-tickets": "Support Tickets",
      "sidebar-menu-administration-system-notifications": "System Notifications",
      "sidebar-menu-administration-system-queue": "System Queue",
      "sidebar-menu-support": "Support",
      "sidebar-menu-support-my-tickets": "My Tickets",
      "sidebar-menu-support-chat": "Chat",
      "sidebar-menu-support-email": "Email",
      "sidebar-menu-anamnesis": "Anamnesis",
      "sidebar-menu-anamnesis-settings": "Settings",
      "sidebar-menu-anamnesis-knowledge-graphs": "Knowledge Graphs",
      "sidebar-menu-anamnesis-ai-prompts": "AI Prompts",
      "sidebar-menu-beamonscreen": "Beam on Screen",
      "sidebar-menu-beamonscreen-assets": "Assets",
      "sidebar-menu-beamonscreen-reel-maker": "Reel Maker",
      "sidebar-menu-beamonscreen-manage-screens": "Manage Screens",
      "sidebar-menu-beamonscreen-playlists": "Playlists",
      "sidebar-menu-infralytiqs": "Infralytiqs",
      "sidebar-menu-infralytiqs-settings": "Settings",
      "sidebar-menu-infralytiqs-reports": "Reports",
      "sidebar-menu-infralytiqs-logs": "Logs",
      "sidebar-menu-infralytiqs-audit-logs": "Audit Logs",
      "sidebar-menu-analytics": "Analytics",
      "sidebar-menu-analytics-settings": "Settings",
      "sidebar-menu-analytics-reports": "Reports",
      "sidebar-menu-analytics-audit-logs": "Audit Logs",
      "modules-instantupdate-dashboard-main-title": "Campaigns Module Dashboard",
      "modules-instantupdate-dashboard-total-templates": "Total Templates",
      "modules-instantupdate-dashboard-total-datasets": "Total Data Sets",
      "modules-instantupdate-dashboard-total-campaigns": "Total Campaigns",
      "modules-instantupdate-dashboard-total-campaigns-subtitle": "Created at company level",
      "modules-instantupdate-dashboard-total-completed": "Total Completed",
      "modules-instantupdate-dashboard-total-scheduled": "Total Scheduled",
      "modules-instantupdate-dashboard-templates-creation-graph-title": "Template Creation",
      "modules-instantupdate-dashboard-datasets-creation-graph-title": "Data Sets",
      "modules-instantupdate-dashboard-campaigns-creation-graph-title": "Schedule Campaigns",
      "modules-instantupdate-dashboard-scheduler-stats-graph-title": "Campaign Stats",
      "modules-instantupdate-dashboard-whatsapp-campaign-summary-graph-title": "WhatsApp Campaign Summary",
      "modules-instantupdate-dashboard-email-campaign-summary-graph-title": "Email Campaign Summary",
      "modules-settings-whatsapp-offline-subtitle": "Schedule new WhatsApp Message to bring it 'Online'.",
      "modules-settings-whatsapp-offline-title": "WhatsApp BOT 'Offline'",
      "modules-settings-whatsapp-online-subtitle": "Your WhatsApp bot is online and ready to send notifications messages!",
      "modules-settings-whatsapp-online-title": "WhatsApp BOT 'Online'",
      "modules-settings-whatsapp-busy-subtitle": "WhatsApp Bot is currently busy. Please wait or try again later.",
      "modules-settings-whatsapp-busy-title": "WhatsApp BOT 'Busy'",
      "modules-settings-whatsapp-not-configured-subtitle": "Please contact your administrator to initiate the WhatsApp Bot.",
      "modules-settings-whatsapp-not-configured-title": "WhatsApp BOT 'Not Configured'",
      "modules-settings-whatsapp-no-qr-code-available": "No QR Code Available",
      "modules-settings-whatsapp-start-the-whatsapp-bot-to-generate-qr-code": "Start the WhatsApp bot to generate QR code",
      "modules-settings-whatsapp-qr-code-error": "QR Code Error",
      "modules-settings-whatsapp-generating-qr-code": "Generating QR Code...",
      "modules-settings-whatsapp-access-denied": "Access Denied",
      "modules-settings-whatsapp-you-dont-have-permission-to-access-whatsapp-configuration": "You don't have permission to access WhatsApp configuration.",
      "modules-settings-whatsapp-whatsapp-configuration-saved-successfully": "WhatsApp configuration saved successfully!",
      "modules-settings-whatsapp-loading-whatsapp-configuration": "Loading WhatsApp Configuration",
      "modules-settings-whatsapp-please-wait": "Please wait...",
      "modules-settings-whatsapp-fetching-latest-configuration-from-server": "Fetching latest configuration from server",
      "modules-settings-whatsapp-scan-qr-code": "Scan QR Code",
      "modules-settings-whatsapp-scan-to-connect-whatsapp-bot": "Scan to connect WhatsApp bot",
      "modules-settings-whatsapp-qr-code-is-ready-for-scanning": "QR code is ready for scanning",
      "modules-settings-whatsapp-your-whatsapp-bot-is-online-and-ready-to-send-campaign-messages": "Your WhatsApp bot is online and ready to send notifications messages!",
      "modules-settings-whatsapp-please-contact-your-administrator-to-initiate-the-whatsapp-bot": "Please contact your administrator to initiate the WhatsApp Bot.",
      "modules-instantupdate-runner-access-denied-title": "Access Denied",
      "modules-instantupdate-runner-access-denied-subtitle": "You don't have permission to access the Campaign Runner.",
      "modules-instantupdate-runner-empty-title": "No Templates or Datasets",
      "modules-instantupdate-runner-empty-subtitle": "Please create at least one template and one dataset to use the Campaign Runner.",
      "modules-instantupdate-runner-breadcrumbs-runner": "Runner",
      "modules-instantupdate-runner-page-title": "Campaign Runner",
      "modules-instantupdate-runner-page-subtitle": "Schedule and manage your marketing campaigns",
      "modules-instantupdate-runner-button-refresh": "Refresh",
      "modules-instantupdate-runner-button-new-campaign": "New Campaign",
      "modules-instantupdate-runner-action-view-report": "View Report",
      "modules-instantupdate-runner-action-edit": "Edit Campaign",
      "modules-instantupdate-runner-action-delete": "Delete Campaign",
      "modules-instantupdate-runner-action-stop": "Stop Campaign",
      "modules-instantupdate-runner-status-scheduled": "Scheduled",
      "modules-instantupdate-runner-status-running": "Running",
      "modules-instantupdate-runner-status-queued": "Queued",
      "modules-instantupdate-runner-status-completed": "Completed",
      "modules-instantupdate-runner-status-cancelled": "Cancelled",
      "modules-instantupdate-runner-status-stopped": "Stopped",
      "modules-instantupdate-runner-status-blocked": "Blocked",
      "modules-instantupdate-runner-unknown-template": "Unknown Template",
      "modules-instantupdate-runner-unknown-dataset": "Unknown Dataset",
      "modules-instantupdate-runner-unknown-user": "Unknown User",
      "modules-instantupdate-runner-steps-select-template": "Select Template",
      "modules-instantupdate-runner-steps-select-dataset": "Select DataSet",
      "modules-instantupdate-runner-steps-configure-conditions": "Configure Conditions",
      "modules-instantupdate-runner-steps-set-interval": "Set Interval",
      "modules-instantupdate-runner-steps-schedule": "Schedule Campaign",
      "modules-instantupdate-runner-select-template-title": "Select Template",
      "modules-instantupdate-runner-select-template-subtitle": "Choose a template for your campaign message",
      "modules-instantupdate-runner-select-template-label": "Template *",
      "modules-instantupdate-runner-select-template-helper": "Select a template to continue",
      "modules-instantupdate-runner-wa-telegram-risk-panel-title": "Use at your own RISK",
      "modules-instantupdate-runner-wa-telegram-risk-warning": "Campaigns of type WhatsApp / Telegram must be used on your own risk, as we only recommend using these type of campaign for your KNOWN WhatsApp / Telegram contacts - with 60+ seconds interval between messages in Step 4. Failing to do so or sending messages to UNKNOWN contacts may or will lead to suspension of your WhatsApp / Telegram Account by Meta / Telegram for 24 hours, AND we will NOT take responsibility for getting this UNBLOCKED. Please use this at your own risk with UNKNOWN Contacts.\nFor information, contact support@campaignking.ai",
      "modules-instantupdate-runner-select-dataset-title": "Select Dataset",
      "modules-instantupdate-runner-select-dataset-subtitle": "Choose the dataset containing your target audience",
      "modules-instantupdate-runner-select-dataset-label": "Dataset *",
      "modules-instantupdate-runner-select-dataset-helper": "Select a dataset to continue",
      "modules-instantupdate-runner-configure-conditions-title": "Configure Campaign Conditions",
      "modules-instantupdate-runner-consent-requirements": "Consent Requirements",
      "modules-instantupdate-runner-target-audience": "Target Audience",
      "modules-instantupdate-runner-target-audience-all": "Send to all contacts",
      "modules-instantupdate-runner-target-audience-consent": "Send to contacts with consent",
      "modules-instantupdate-runner-target-audience-my-contacts": "Send to my contacts only",
      "modules-instantupdate-runner-target-audience-my-contacts-consent": "Send to my contacts only with consent",
      "modules-instantupdate-runner-filtering-conditions-title": "Filtering Conditions",
      "modules-instantupdate-runner-condition-logic-label": "Condition Logic",
      "modules-instantupdate-runner-condition-logic-and": "AND (All conditions must be met)",
      "modules-instantupdate-runner-condition-logic-or": "OR (Any condition can be met)",
      "modules-instantupdate-runner-condition-column-label": "Column",
      "modules-instantupdate-runner-condition-operator-label": "Operator",
      "modules-instantupdate-runner-condition-operator-equals": "Equal to",
      "modules-instantupdate-runner-condition-operator-not-equals": "Not Equal to",
      "modules-instantupdate-runner-condition-operator-less-than": "Less than",
      "modules-instantupdate-runner-condition-operator-less-than-equal": "Less than or equal to",
      "modules-instantupdate-runner-condition-operator-greater-than": "Greater than",
      "modules-instantupdate-runner-condition-operator-greater-than-equal": "Greater than or equal to",
      "modules-instantupdate-runner-condition-operator-contains": "Contains",
      "modules-instantupdate-runner-condition-value-label": "Value",
      "modules-instantupdate-runner-button-add-condition": "Add Condition",
      "modules-instantupdate-runner-select-dataset-prompt": "Please select a dataset first to configure conditions.",
      "modules-instantupdate-runner-set-interval-title": "Set Time Interval",
      "modules-instantupdate-runner-set-interval-subtitle": "Configure the delay between campaign messages",
      "modules-instantupdate-runner-time-interval-label": "Time Interval (seconds) *",
      "modules-instantupdate-runner-time-interval-helper-wa-min": "For WhatsApp, recommended minimum is 60 seconds.",
      "modules-instantupdate-runner-time-interval-helper-general": "Enter time interval between campaign messages (minimum 1 second)",
      "modules-instantupdate-runner-seconds-suffix": "seconds",
      "modules-instantupdate-runner-schedule-title": "Schedule Campaign",
      "modules-instantupdate-runner-schedule-subtitle": "Set campaign details and schedule",
      "modules-instantupdate-runner-campaign-name-label": "Campaign Name *",
      "modules-instantupdate-runner-campaign-name-helper": "Enter a name for your campaign",
      "modules-instantupdate-runner-description-label": "Description (Optional)",
      "modules-instantupdate-runner-start-timestamp-label": "Start Timestamp *",
      "modules-instantupdate-runner-start-timestamp-helper": "Select when the campaign should start",
      "modules-instantupdate-runner-mobile-step-label": "Step",
      "modules-instantupdate-runner-mobile-step-of": "of",
      "modules-instantupdate-runner-button-cancel": "Cancel",
      "modules-instantupdate-runner-button-back": "Back",
      "modules-instantupdate-runner-button-update": "Update Campaign",
      "modules-instantupdate-runner-button-submit": "Submit",
      "modules-instantupdate-runner-button-next": "Next",
      "modules-instantupdate-runner-snackbar-updated": "Campaign updated successfully!",
      "modules-instantupdate-runner-snackbar-created": "Campaign created successfully!",
      "modules-instantupdate-runner-snackbar-deleted": "Campaign deleted successfully!",
      "modules-instantupdate-runner-snackbar-stopped": "Campaign stopped successfully!",
      "modules-instantupdate-runner-delete-dialog-title": "Confirm Deletion",
      "modules-instantupdate-runner-delete-dialog-description-prefix": "Are you sure you want to delete campaign",
      "modules-instantupdate-runner-delete-dialog-button-delete": "Delete",
      "modules-instantupdate-runner-stop-dialog-title": "Confirm Stop Campaign",
      "modules-instantupdate-runner-stop-dialog-description-prefix": "Are you sure you want to stop campaign",
      "modules-instantupdate-runner-stop-dialog-description-suffix": "This action cannot be undone. Once stopped, this campaign cannot be restarted and you will need to start a new campaign if you wish to continue.",
      "modules-instantupdate-runner-stop-dialog-button-stop": "Stop Campaign",
      "modules-instantupdate-runner-report-campaign-information-title": "Campaign Information",
      "modules-instantupdate-runner-report-breadcrumb-runner": "Runner",
      "modules-instantupdate-runner-report-breadcrumb-report": "Campaign Report",
      "modules-instantupdate-runner-new-dialogbox-create-title": "Create New Campaign",
      "modules-instantupdate-runner-new-dialogbox-edit-title": "Edit Campaign",
      "modules-instantupdate-runner-table-column-start-timestamp": "Start Timestamp",
      "modules-instantupdate-runner-table-column-status": "Status",
      "modules-instantupdate-runner-table-column-time-interval": "Time Interval",
      "modules-instantupdate-runner-table-column-created-on": "Created On",
      "modules-instantupdate-runner-table-column-campaign-name": "Campaign Name",
      "modules-instantupdate-runner-table-column-campaign-type": "Campaign Type",
      "modules-instantupdate-runner-table-column-template-name": "Template Name",
      "modules-instantupdate-runner-table-column-dataset-name": "Data Set Name",
      "modules-instantupdate-runner-table-column-created-by-user-name": "Created By",
      "modules-instantupdate-runner-table-column-actions": "Actions",
      "modules-instantupdate-runner-tab-scheduled": "Scheduled",
      "modules-instantupdate-runner-tab-in-progress": "In Progress",
      "modules-instantupdate-runner-tab-completed": "Completed",
    },
    fr: {
      
    },
  },
  "my-local.instantupdate.ai": {
    en: {
      "sidebar-menu-dashboard": "Dashboard",
      "sidebar-menu-settings": "Settings",
      "sidebar-menu-settings-profile": "Profile",
      "sidebar-menu-settings-security": "Security",
      "sidebar-menu-settings-notifications": "Notifications",
      "sidebar-menu-settings-users": "Users",
      "sidebar-menu-settings-companies": "Companies",
      "sidebar-menu-settings-whatsapp": "WhatsApp",
      "sidebar-menu-settings-whatsappbusiness": "WhatsApp Business",
      "sidebar-menu-settings-telegram": "Telegram",
      "sidebar-menu-settings-sms": "GSM Text",
      "sidebar-menu-settings-email": "Email",
      "sidebar-menu-settings-bot-manager": "BOT Manager",
      "sidebar-menu-settings-openai": "OpenAI",
      "sidebar-menu-settings-youtube": "YouTube",
      "sidebar-menu-settings-instagram": "Instagram",
      "sidebar-menu-settings-tiktok": "TikTok",
      "sidebar-menu-settings-signal": "Signal",
      "sidebar-menu-instant-updates": "Instant Updates",
      "sidebar-menu-instant-updates-data-sets": "Data Sets",
      "sidebar-menu-instant-updates-templates": "Templates",
      "sidebar-menu-instant-updates-runner": "Scheduler",
      "sidebar-menu-instant-updates-push-notifications": "Push Notifications",
      "sidebar-menu-administration-domains": "Domains",
      "sidebar-menu-administration-support-tickets": "Support Tickets",
      "sidebar-menu-administration-system-notifications": "System Notifications",
      "sidebar-menu-administration-system-queue": "System Queue",
      "sidebar-menu-support": "Support",
      "sidebar-menu-support-my-tickets": "My Tickets",
      "sidebar-menu-support-chat": "Chat",
      "sidebar-menu-support-email": "Email",
      "sidebar-menu-anamnesis": "Anamnesis",
      "sidebar-menu-anamnesis-settings": "Settings",
      "sidebar-menu-anamnesis-knowledge-graphs": "Knowledge Graphs",
      "sidebar-menu-anamnesis-ai-prompts": "AI Prompts",
      "sidebar-menu-beamonscreen": "Beam on Screen",
      "sidebar-menu-beamonscreen-assets": "Assets",
      "sidebar-menu-beamonscreen-reel-maker": "Reel Maker",
      "sidebar-menu-beamonscreen-manage-screens": "Manage Screens",
      "sidebar-menu-beamonscreen-playlists": "Playlists",
      "sidebar-menu-infralytiqs": "Infralytiqs",
      "sidebar-menu-infralytiqs-settings": "Settings",
      "sidebar-menu-infralytiqs-reports": "Reports",
      "sidebar-menu-infralytiqs-logs": "Logs",
      "sidebar-menu-infralytiqs-audit-logs": "Audit Logs",
      "sidebar-menu-analytics": "Analytics",
      "sidebar-menu-analytics-settings": "Settings",
      "sidebar-menu-analytics-reports": "Reports",
      "sidebar-menu-analytics-audit-logs": "Audit Logs",
      "modules-instantupdate-dashboard-main-title": "Instant Update Module Dashboard",
      "modules-instantupdate-dashboard-total-templates": "Total Templates",
      "modules-instantupdate-dashboard-total-datasets": "Total Data Sets",
      "modules-instantupdate-dashboard-total-campaigns": "Total Notifications",
      "modules-instantupdate-dashboard-total-campaigns-subtitle": "Created at company level",
      "modules-instantupdate-dashboard-total-completed": "Total Completed",
      "modules-instantupdate-dashboard-total-scheduled": "Total Scheduled",
      "modules-instantupdate-dashboard-templates-creation-graph-title": "Template Creation",
      "modules-instantupdate-dashboard-datasets-creation-graph-title": "Data Sets",
      "modules-instantupdate-dashboard-campaigns-creation-graph-title": "Schedule Items",
      "modules-instantupdate-dashboard-scheduler-stats-graph-title": "Notification Stats",
      "modules-instantupdate-dashboard-whatsapp-campaign-summary-graph-title": "WhatsApp Notifications Summary",
      "modules-instantupdate-dashboard-email-campaign-summary-graph-title": "Email Notifications Summary",
      "modules-settings-whatsapp-offline-subtitle": "Schedule new WhatsApp Message to bring it 'Online'.",
      "modules-settings-whatsapp-offline-title": "WhatsApp BOT 'Offline'",
      "modules-settings-whatsapp-online-subtitle": "Your WhatsApp bot is online and ready to send notifications messages!",
      "modules-settings-whatsapp-online-title": "WhatsApp BOT 'Online'",
      "modules-settings-whatsapp-busy-subtitle": "WhatsApp Bot is currently busy. Please wait or try again later.",
      "modules-settings-whatsapp-busy-title": "WhatsApp BOT 'Busy'",
      "modules-settings-whatsapp-not-configured-subtitle": "Please contact your administrator to initiate the WhatsApp Bot.",
      "modules-settings-whatsapp-not-configured-title": "WhatsApp BOT 'Not Configured'",
      "modules-settings-whatsapp-no-qr-code-available": "No QR Code Available",
      "modules-settings-whatsapp-start-the-whatsapp-bot-to-generate-qr-code": "Start the WhatsApp bot to generate QR code",
      "modules-settings-whatsapp-qr-code-error": "QR Code Error",
      "modules-settings-whatsapp-generating-qr-code": "Generating QR Code...",
      "modules-settings-whatsapp-access-denied": "Access Denied",
      "modules-settings-whatsapp-you-dont-have-permission-to-access-whatsapp-configuration": "You don't have permission to access WhatsApp configuration.",
      "modules-settings-whatsapp-whatsapp-configuration-saved-successfully": "WhatsApp configuration saved successfully!",
      "modules-settings-whatsapp-loading-whatsapp-configuration": "Loading WhatsApp Configuration",
      "modules-settings-whatsapp-please-wait": "Please wait...",
      "modules-settings-whatsapp-fetching-latest-configuration-from-server": "Fetching latest configuration from server",
      "modules-settings-whatsapp-scan-qr-code": "Scan QR Code",
      "modules-settings-whatsapp-scan-to-connect-whatsapp-bot": "Scan to connect WhatsApp bot",
      "modules-settings-whatsapp-qr-code-is-ready-for-scanning": "QR code is ready for scanning",
      "modules-settings-whatsapp-your-whatsapp-bot-is-online-and-ready-to-send-campaign-messages": "Your WhatsApp bot is online and ready to send notifications messages!",
      "modules-settings-whatsapp-please-contact-your-administrator-to-initiate-the-whatsapp-bot": "Please contact your administrator to initiate the WhatsApp Bot.",
      "modules-instantupdate-runner-table-column-start-timestamp": "Start Timestamp",
      "modules-instantupdate-runner-table-column-status": "Status",
      "modules-instantupdate-runner-table-column-time-interval": "Time Interval",
      "modules-instantupdate-runner-table-column-created-on": "Created On",
      "modules-instantupdate-runner-table-column-campaign-name": "Notification Name",
      "modules-instantupdate-runner-table-column-campaign-type": "Notification Type",
      "modules-instantupdate-runner-table-column-template-name": "Template Name",
      "modules-instantupdate-runner-table-column-dataset-name": "Data Set Name",
      "modules-instantupdate-runner-table-column-created-by-user-name": "Created By",
      "modules-instantupdate-runner-table-column-actions": "Actions",
      "modules-instantupdate-runner-tab-scheduled": "Scheduled",
      "modules-instantupdate-runner-tab-in-progress": "In Progress",
      "modules-instantupdate-runner-tab-completed": "Completed",
      "modules-instantupdate-runner-access-denied-title": "Access Denied",
      "modules-instantupdate-runner-access-denied-subtitle": "You don't have permission to access the Scheduler.",
      "modules-instantupdate-runner-empty-title": "No Templates or Datasets",
      "modules-instantupdate-runner-empty-subtitle": "Please create at least one template and one dataset to use the Scheduler.",
      "modules-instantupdate-runner-breadcrumbs-runner": "Scheduler",
      "modules-instantupdate-runner-page-title": "Scheduler",
      "modules-instantupdate-runner-page-subtitle": "Schedule and manage your Notifications",
      "modules-instantupdate-runner-button-refresh": "Refresh",
      "modules-instantupdate-runner-button-new-campaign": "New Notification",
      "modules-instantupdate-runner-action-view-report": "View Report",
      "modules-instantupdate-runner-action-edit": "Edit Notification",
      "modules-instantupdate-runner-action-delete": "Delete Notification",
      "modules-instantupdate-runner-action-stop": "Stop Notification",
      "modules-instantupdate-runner-status-scheduled": "Scheduled",
      "modules-instantupdate-runner-status-running": "Running",
      "modules-instantupdate-runner-status-queued": "Queued",
      "modules-instantupdate-runner-status-completed": "Completed",
      "modules-instantupdate-runner-status-cancelled": "Cancelled",
      "modules-instantupdate-runner-status-stopped": "Stopped",
      "modules-instantupdate-runner-status-blocked": "Blocked",
      "modules-instantupdate-runner-unknown-template": "Unknown Template",
      "modules-instantupdate-runner-unknown-dataset": "Unknown Dataset",
      "modules-instantupdate-runner-unknown-user": "Unknown User",
      "modules-instantupdate-runner-steps-select-template": "Select Template",
      "modules-instantupdate-runner-steps-select-dataset": "Select DataSet",
      "modules-instantupdate-runner-steps-configure-conditions": "Configure Conditions",
      "modules-instantupdate-runner-steps-set-interval": "Set Interval",
      "modules-instantupdate-runner-steps-schedule": "Schedule Notification",
      "modules-instantupdate-runner-select-template-title": "Select Template",
      "modules-instantupdate-runner-select-template-subtitle": "Choose a template for your Notification message",
      "modules-instantupdate-runner-select-template-label": "Template *",
      "modules-instantupdate-runner-select-template-helper": "Select a template to continue",
      "modules-instantupdate-runner-wa-telegram-risk-panel-title": "Use at your own RISK",
      "modules-instantupdate-runner-wa-telegram-risk-warning": "Notifications of type WhatsApp / Telegram must be used on your own risk, as we only recommend using these type of notifications for your KNOWN WhatsApp / Telegram contacts - with 60+ seconds interval between messages in Step 4. Failing to do so or sending messages to UNKNOWN contacts may or will lead to suspension of your WhatsApp / Telegram Account by Meta / Telegram for 24 hours, AND we will NOT take responsibility for getting this UNBLOCKED. Please use this at your own risk with UNKNOWN Contacts.\nFor information, contact support@instantupdate.ai",
      "modules-instantupdate-runner-select-dataset-title": "Select Dataset",
      "modules-instantupdate-runner-select-dataset-subtitle": "Choose the dataset containing your target audience",
      "modules-instantupdate-runner-select-dataset-label": "Dataset *",
      "modules-instantupdate-runner-select-dataset-helper": "Select a dataset to continue",
      "modules-instantupdate-runner-configure-conditions-title": "Configure Notification Conditions",
      "modules-instantupdate-runner-consent-requirements": "Consent Requirements",
      "modules-instantupdate-runner-target-audience": "Target Audience",
      "modules-instantupdate-runner-target-audience-all": "Send to all contacts",
      "modules-instantupdate-runner-target-audience-consent": "Send to contacts with consent",
      "modules-instantupdate-runner-target-audience-my-contacts": "Send to my contacts only",
      "modules-instantupdate-runner-target-audience-my-contacts-consent": "Send to my contacts only with consent",
      "modules-instantupdate-runner-filtering-conditions-title": "Filtering Conditions",
      "modules-instantupdate-runner-condition-logic-label": "Condition Logic",
      "modules-instantupdate-runner-condition-logic-and": "AND (All conditions must be met)",
      "modules-instantupdate-runner-condition-logic-or": "OR (Any condition can be met)",
      "modules-instantupdate-runner-condition-column-label": "Column",
      "modules-instantupdate-runner-condition-operator-label": "Operator",
      "modules-instantupdate-runner-condition-operator-equals": "Equal to",
      "modules-instantupdate-runner-condition-operator-not-equals": "Not Equal to",
      "modules-instantupdate-runner-condition-operator-less-than": "Less than",
      "modules-instantupdate-runner-condition-operator-less-than-equal": "Less than or equal to",
      "modules-instantupdate-runner-condition-operator-greater-than": "Greater than",
      "modules-instantupdate-runner-condition-operator-greater-than-equal": "Greater than or equal to",
      "modules-instantupdate-runner-condition-operator-contains": "Contains",
      "modules-instantupdate-runner-condition-value-label": "Value",
      "modules-instantupdate-runner-button-add-condition": "Add Condition",
      "modules-instantupdate-runner-select-dataset-prompt": "Please select a dataset first to configure conditions.",
      "modules-instantupdate-runner-set-interval-title": "Set Time Interval",
      "modules-instantupdate-runner-set-interval-subtitle": "Configure the delay between notifications messages",
      "modules-instantupdate-runner-time-interval-label": "Time Interval (seconds) *",
      "modules-instantupdate-runner-time-interval-helper-wa-min": "For WhatsApp, recommended minimum is 60 seconds.",
      "modules-instantupdate-runner-time-interval-helper-general": "Enter time interval between notifications messages (minimum 1 second)",
      "modules-instantupdate-runner-seconds-suffix": "seconds",
      "modules-instantupdate-runner-schedule-title": "Schedule Notification",
      "modules-instantupdate-runner-schedule-subtitle": "Set notification details and schedule",
      "modules-instantupdate-runner-campaign-name-label": "Notification Name *",
      "modules-instantupdate-runner-campaign-name-helper": "Enter a name for your notification",
      "modules-instantupdate-runner-description-label": "Description (Optional)",
      "modules-instantupdate-runner-start-timestamp-label": "Start Timestamp *",
      "modules-instantupdate-runner-start-timestamp-helper": "Select when the notification should start",
      "modules-instantupdate-runner-mobile-step-label": "Step",
      "modules-instantupdate-runner-mobile-step-of": "of",
      "modules-instantupdate-runner-button-cancel": "Cancel",
      "modules-instantupdate-runner-button-back": "Back",
      "modules-instantupdate-runner-button-update": "Update Notification",
      "modules-instantupdate-runner-button-submit": "Submit",
      "modules-instantupdate-runner-button-next": "Next",
      "modules-instantupdate-runner-snackbar-updated": "Notification updated successfully!",
      "modules-instantupdate-runner-snackbar-created": "Notification created successfully!",
      "modules-instantupdate-runner-snackbar-deleted": "Notification deleted successfully!",
      "modules-instantupdate-runner-snackbar-stopped": "Notification stopped successfully!",
      "modules-instantupdate-runner-delete-dialog-title": "Confirm Deletion",
      "modules-instantupdate-runner-delete-dialog-description-prefix": "Are you sure you want to delete notification",
      "modules-instantupdate-runner-delete-dialog-button-delete": "Delete",
      "modules-instantupdate-runner-stop-dialog-title": "Confirm Stop Notification",
      "modules-instantupdate-runner-stop-dialog-description-prefix": "Are you sure you want to stop notification",
      "modules-instantupdate-runner-stop-dialog-description-suffix": "This action cannot be undone. Once stopped, this notification cannot be restarted and you will need to start a new notification if you wish to continue.",
      "modules-instantupdate-runner-stop-dialog-button-stop": "Stop Notification",
      "modules-instantupdate-runner-report-campaign-information-title": "Notification Information",
      "modules-instantupdate-runner-report-breadcrumb-runner": "Scheduler",
      "modules-instantupdate-runner-report-breadcrumb-report": "Notification Report",
      "modules-instantupdate-runner-new-dialogbox-create-title": "Create New Notification",
      "modules-instantupdate-runner-new-dialogbox-edit-title": "Edit Notification",
    },
    fr: {
      
    },
  },

  //STAGE
  "my-stage.campaignking.ai": {
    en: {
      "sidebar-menu-dashboard": "Dashboard",
      "sidebar-menu-settings": "Settings",
      "sidebar-menu-settings-profile": "Profile",
      "sidebar-menu-settings-security": "Security",
      "sidebar-menu-settings-notifications": "Notifications",
      "sidebar-menu-settings-users": "Users",
      "sidebar-menu-settings-companies": "Companies",
      "sidebar-menu-settings-whatsapp": "WhatsApp",
      "sidebar-menu-settings-whatsappbusiness": "WhatsApp Business",
      "sidebar-menu-settings-telegram": "Telegram",
      "sidebar-menu-settings-sms": "GSM Text",
      "sidebar-menu-settings-email": "Email",
      "sidebar-menu-settings-bot-manager": "BOT Manager",
      "sidebar-menu-settings-openai": "OpenAI",
      "sidebar-menu-settings-youtube": "YouTube",
      "sidebar-menu-settings-instagram": "Instagram",
      "sidebar-menu-settings-tiktok": "TikTok",
      "sidebar-menu-settings-signal": "Signal",
      "sidebar-menu-instant-updates": "Campaigns",
      "sidebar-menu-instant-updates-data-sets": "Data Sets",
      "sidebar-menu-instant-updates-templates": "Templates",
      "sidebar-menu-instant-updates-runner": "Runner",
      "sidebar-menu-instant-updates-activities": "Activities",
      "sidebar-menu-administration-domains": "Domains",
      "sidebar-menu-administration-support-tickets": "Support Tickets",
      "sidebar-menu-administration-system-notifications": "System Notifications",
      "sidebar-menu-administration-system-queue": "System Queue",
      "sidebar-menu-support": "Support",
      "sidebar-menu-support-my-tickets": "My Tickets",
      "sidebar-menu-support-chat": "Chat",
      "sidebar-menu-support-email": "Email",
      "sidebar-menu-anamnesis": "Anamnesis",
      "sidebar-menu-anamnesis-settings": "Settings",
      "sidebar-menu-anamnesis-knowledge-graphs": "Knowledge Graphs",
      "sidebar-menu-anamnesis-ai-prompts": "AI Prompts",
      "sidebar-menu-beamonscreen": "Beam on Screen",
      "sidebar-menu-beamonscreen-assets": "Assets",
      "sidebar-menu-beamonscreen-reel-maker": "Reel Maker",
      "sidebar-menu-beamonscreen-manage-screens": "Manage Screens",
      "sidebar-menu-beamonscreen-playlists": "Playlists",
      "sidebar-menu-infralytiqs": "Infralytiqs",
      "sidebar-menu-infralytiqs-settings": "Settings",
      "sidebar-menu-infralytiqs-reports": "Reports",
      "sidebar-menu-infralytiqs-logs": "Logs",
      "sidebar-menu-infralytiqs-audit-logs": "Audit Logs",
      "sidebar-menu-analytics": "Analytics",
      "sidebar-menu-analytics-settings": "Settings",
      "sidebar-menu-analytics-reports": "Reports",
      "sidebar-menu-analytics-audit-logs": "Audit Logs",
      "modules-instantupdate-dashboard-main-title": "Campaigns Module Dashboard",
      "modules-instantupdate-dashboard-total-templates": "Total Templates",
      "modules-instantupdate-dashboard-total-datasets": "Total Data Sets",
      "modules-instantupdate-dashboard-total-campaigns": "Total Campaigns",
      "modules-instantupdate-dashboard-total-campaigns-subtitle": "Created at company level",
      "modules-instantupdate-dashboard-total-completed": "Total Completed",
      "modules-instantupdate-dashboard-total-scheduled": "Total Scheduled",
      "modules-instantupdate-dashboard-templates-creation-graph-title": "Template Creation",
      "modules-instantupdate-dashboard-datasets-creation-graph-title": "Data Sets",
      "modules-instantupdate-dashboard-campaigns-creation-graph-title": "Schedule Campaigns",
      "modules-instantupdate-dashboard-scheduler-stats-graph-title": "Campaign Stats",
      "modules-instantupdate-dashboard-whatsapp-campaign-summary-graph-title": "WhatsApp Campaign Summary",
      "modules-instantupdate-dashboard-email-campaign-summary-graph-title": "Email Campaign Summary",
      "modules-settings-whatsapp-offline-subtitle": "Schedule new WhatsApp Message to bring it 'Online'.",
      "modules-settings-whatsapp-offline-title": "WhatsApp BOT 'Offline'",
      "modules-settings-whatsapp-online-subtitle": "Your WhatsApp bot is online and ready to send notifications messages!",
      "modules-settings-whatsapp-online-title": "WhatsApp BOT 'Online'",
      "modules-settings-whatsapp-busy-subtitle": "WhatsApp Bot is currently busy. Please wait or try again later.",
      "modules-settings-whatsapp-busy-title": "WhatsApp BOT 'Busy'",
      "modules-settings-whatsapp-not-configured-subtitle": "Please contact your administrator to initiate the WhatsApp Bot.",
      "modules-settings-whatsapp-not-configured-title": "WhatsApp BOT 'Not Configured'",
      "modules-settings-whatsapp-no-qr-code-available": "No QR Code Available",
      "modules-settings-whatsapp-start-the-whatsapp-bot-to-generate-qr-code": "Start the WhatsApp bot to generate QR code",
      "modules-settings-whatsapp-qr-code-error": "QR Code Error",
      "modules-settings-whatsapp-generating-qr-code": "Generating QR Code...",
      "modules-settings-whatsapp-access-denied": "Access Denied",
      "modules-settings-whatsapp-you-dont-have-permission-to-access-whatsapp-configuration": "You don't have permission to access WhatsApp configuration.",
      "modules-settings-whatsapp-whatsapp-configuration-saved-successfully": "WhatsApp configuration saved successfully!",
      "modules-settings-whatsapp-loading-whatsapp-configuration": "Loading WhatsApp Configuration",
      "modules-settings-whatsapp-please-wait": "Please wait...",
      "modules-settings-whatsapp-fetching-latest-configuration-from-server": "Fetching latest configuration from server",
      "modules-settings-whatsapp-scan-qr-code": "Scan QR Code",
      "modules-settings-whatsapp-scan-to-connect-whatsapp-bot": "Scan to connect WhatsApp bot",
      "modules-settings-whatsapp-qr-code-is-ready-for-scanning": "QR code is ready for scanning",
      "modules-settings-whatsapp-your-whatsapp-bot-is-online-and-ready-to-send-campaign-messages": "Your WhatsApp bot is online and ready to send notifications messages!",
      "modules-settings-whatsapp-please-contact-your-administrator-to-initiate-the-whatsapp-bot": "Please contact your administrator to initiate the WhatsApp Bot.",
      "modules-instantupdate-runner-access-denied-title": "Access Denied",
      "modules-instantupdate-runner-access-denied-subtitle": "You don't have permission to access the Campaign Runner.",
      "modules-instantupdate-runner-empty-title": "No Templates or Datasets",
      "modules-instantupdate-runner-empty-subtitle": "Please create at least one template and one dataset to use the Campaign Runner.",
      "modules-instantupdate-runner-breadcrumbs-runner": "Runner",
      "modules-instantupdate-runner-page-title": "Campaign Runner",
      "modules-instantupdate-runner-page-subtitle": "Schedule and manage your marketing campaigns",
      "modules-instantupdate-runner-button-refresh": "Refresh",
      "modules-instantupdate-runner-button-new-campaign": "New Campaign",
      "modules-instantupdate-runner-action-view-report": "View Report",
      "modules-instantupdate-runner-action-edit": "Edit Campaign",
      "modules-instantupdate-runner-action-delete": "Delete Campaign",
      "modules-instantupdate-runner-action-stop": "Stop Campaign",
      "modules-instantupdate-runner-status-scheduled": "Scheduled",
      "modules-instantupdate-runner-status-running": "Running",
      "modules-instantupdate-runner-status-queued": "Queued",
      "modules-instantupdate-runner-status-completed": "Completed",
      "modules-instantupdate-runner-status-cancelled": "Cancelled",
      "modules-instantupdate-runner-status-stopped": "Stopped",
      "modules-instantupdate-runner-status-blocked": "Blocked",
      "modules-instantupdate-runner-unknown-template": "Unknown Template",
      "modules-instantupdate-runner-unknown-dataset": "Unknown Dataset",
      "modules-instantupdate-runner-unknown-user": "Unknown User",
      "modules-instantupdate-runner-steps-select-template": "Select Template",
      "modules-instantupdate-runner-steps-select-dataset": "Select DataSet",
      "modules-instantupdate-runner-steps-configure-conditions": "Configure Conditions",
      "modules-instantupdate-runner-steps-set-interval": "Set Interval",
      "modules-instantupdate-runner-steps-schedule": "Schedule Campaign",
      "modules-instantupdate-runner-select-template-title": "Select Template",
      "modules-instantupdate-runner-select-template-subtitle": "Choose a template for your campaign message",
      "modules-instantupdate-runner-select-template-label": "Template *",
      "modules-instantupdate-runner-select-template-helper": "Select a template to continue",
      "modules-instantupdate-runner-wa-telegram-risk-panel-title": "Use at your own RISK",
      "modules-instantupdate-runner-wa-telegram-risk-warning": "Campaigns of type WhatsApp / Telegram must be used on your own risk, as we only recommend using these type of campaign for your KNOWN WhatsApp / Telegram contacts - with 60+ seconds interval between messages in Step 4. Failing to do so or sending messages to UNKNOWN contacts may or will lead to suspension of your WhatsApp / Telegram Account by Meta / Telegram for 24 hours, AND we will NOT take responsibility for getting this UNBLOCKED. Please use this at your own risk with UNKNOWN Contacts.\nFor information, contact support@campaignking.ai",
      "modules-instantupdate-runner-select-dataset-title": "Select Dataset",
      "modules-instantupdate-runner-select-dataset-subtitle": "Choose the dataset containing your target audience",
      "modules-instantupdate-runner-select-dataset-label": "Dataset *",
      "modules-instantupdate-runner-select-dataset-helper": "Select a dataset to continue",
      "modules-instantupdate-runner-configure-conditions-title": "Configure Campaign Conditions",
      "modules-instantupdate-runner-consent-requirements": "Consent Requirements",
      "modules-instantupdate-runner-target-audience": "Target Audience",
      "modules-instantupdate-runner-target-audience-all": "Send to all contacts",
      "modules-instantupdate-runner-target-audience-consent": "Send to contacts with consent",
      "modules-instantupdate-runner-target-audience-my-contacts": "Send to my contacts only",
      "modules-instantupdate-runner-target-audience-my-contacts-consent": "Send to my contacts only with consent",
      "modules-instantupdate-runner-filtering-conditions-title": "Filtering Conditions",
      "modules-instantupdate-runner-condition-logic-label": "Condition Logic",
      "modules-instantupdate-runner-condition-logic-and": "AND (All conditions must be met)",
      "modules-instantupdate-runner-condition-logic-or": "OR (Any condition can be met)",
      "modules-instantupdate-runner-condition-column-label": "Column",
      "modules-instantupdate-runner-condition-operator-label": "Operator",
      "modules-instantupdate-runner-condition-operator-equals": "Equal to",
      "modules-instantupdate-runner-condition-operator-not-equals": "Not Equal to",
      "modules-instantupdate-runner-condition-operator-less-than": "Less than",
      "modules-instantupdate-runner-condition-operator-less-than-equal": "Less than or equal to",
      "modules-instantupdate-runner-condition-operator-greater-than": "Greater than",
      "modules-instantupdate-runner-condition-operator-greater-than-equal": "Greater than or equal to",
      "modules-instantupdate-runner-condition-operator-contains": "Contains",
      "modules-instantupdate-runner-condition-value-label": "Value",
      "modules-instantupdate-runner-button-add-condition": "Add Condition",
      "modules-instantupdate-runner-select-dataset-prompt": "Please select a dataset first to configure conditions.",
      "modules-instantupdate-runner-set-interval-title": "Set Time Interval",
      "modules-instantupdate-runner-set-interval-subtitle": "Configure the delay between campaign messages",
      "modules-instantupdate-runner-time-interval-label": "Time Interval (seconds) *",
      "modules-instantupdate-runner-time-interval-helper-wa-min": "For WhatsApp, recommended minimum is 60 seconds.",
      "modules-instantupdate-runner-time-interval-helper-general": "Enter time interval between campaign messages (minimum 1 second)",
      "modules-instantupdate-runner-seconds-suffix": "seconds",
      "modules-instantupdate-runner-schedule-title": "Schedule Campaign",
      "modules-instantupdate-runner-schedule-subtitle": "Set campaign details and schedule",
      "modules-instantupdate-runner-campaign-name-label": "Campaign Name *",
      "modules-instantupdate-runner-campaign-name-helper": "Enter a name for your campaign",
      "modules-instantupdate-runner-description-label": "Description (Optional)",
      "modules-instantupdate-runner-start-timestamp-label": "Start Timestamp *",
      "modules-instantupdate-runner-start-timestamp-helper": "Select when the campaign should start",
      "modules-instantupdate-runner-mobile-step-label": "Step",
      "modules-instantupdate-runner-mobile-step-of": "of",
      "modules-instantupdate-runner-button-cancel": "Cancel",
      "modules-instantupdate-runner-button-back": "Back",
      "modules-instantupdate-runner-button-update": "Update Campaign",
      "modules-instantupdate-runner-button-submit": "Submit",
      "modules-instantupdate-runner-button-next": "Next",
      "modules-instantupdate-runner-snackbar-updated": "Campaign updated successfully!",
      "modules-instantupdate-runner-snackbar-created": "Campaign created successfully!",
      "modules-instantupdate-runner-snackbar-deleted": "Campaign deleted successfully!",
      "modules-instantupdate-runner-snackbar-stopped": "Campaign stopped successfully!",
      "modules-instantupdate-runner-delete-dialog-title": "Confirm Deletion",
      "modules-instantupdate-runner-delete-dialog-description-prefix": "Are you sure you want to delete campaign",
      "modules-instantupdate-runner-delete-dialog-button-delete": "Delete",
      "modules-instantupdate-runner-stop-dialog-title": "Confirm Stop Campaign",
      "modules-instantupdate-runner-stop-dialog-description-prefix": "Are you sure you want to stop campaign",
      "modules-instantupdate-runner-stop-dialog-description-suffix": "This action cannot be undone. Once stopped, this campaign cannot be restarted and you will need to start a new campaign if you wish to continue.",
      "modules-instantupdate-runner-stop-dialog-button-stop": "Stop Campaign",
      "modules-instantupdate-runner-report-campaign-information-title": "Campaign Information",
      "modules-instantupdate-runner-report-breadcrumb-runner": "Runner",
      "modules-instantupdate-runner-report-breadcrumb-report": "Campaign Report",
      "modules-instantupdate-runner-new-dialogbox-create-title": "Create New Campaign",
      "modules-instantupdate-runner-new-dialogbox-edit-title": "Edit Campaign",
      "modules-instantupdate-runner-table-column-start-timestamp": "Start Timestamp",
      "modules-instantupdate-runner-table-column-status": "Status",
      "modules-instantupdate-runner-table-column-time-interval": "Time Interval",
      "modules-instantupdate-runner-table-column-created-on": "Created On",
      "modules-instantupdate-runner-table-column-campaign-name": "Campaign Name",
      "modules-instantupdate-runner-table-column-campaign-type": "Campaign Type",
      "modules-instantupdate-runner-table-column-template-name": "Template Name",
      "modules-instantupdate-runner-table-column-dataset-name": "Data Set Name",
      "modules-instantupdate-runner-table-column-created-by-user-name": "Created By",
      "modules-instantupdate-runner-table-column-actions": "Actions",
      "modules-instantupdate-runner-tab-scheduled": "Scheduled",
      "modules-instantupdate-runner-tab-in-progress": "In Progress",
      "modules-instantupdate-runner-tab-completed": "Completed",
    },
    fr: {
      
    },
  },
  "my-stage.instantupdate.ai": {
    en: {
      "sidebar-menu-dashboard": "Dashboard",
      "sidebar-menu-settings": "Settings",
      "sidebar-menu-settings-profile": "Profile",
      "sidebar-menu-settings-security": "Security",
      "sidebar-menu-settings-notifications": "Notifications",
      "sidebar-menu-settings-users": "Users",
      "sidebar-menu-settings-companies": "Companies",
      "sidebar-menu-settings-whatsapp": "WhatsApp",
      "sidebar-menu-settings-whatsappbusiness": "WhatsApp Business",
      "sidebar-menu-settings-telegram": "Telegram",
      "sidebar-menu-settings-sms": "GSM Text",
      "sidebar-menu-settings-email": "Email",
      "sidebar-menu-settings-bot-manager": "BOT Manager",
      "sidebar-menu-settings-openai": "OpenAI",
      "sidebar-menu-settings-youtube": "YouTube",
      "sidebar-menu-settings-instagram": "Instagram",
      "sidebar-menu-settings-tiktok": "TikTok",
      "sidebar-menu-settings-signal": "Signal",
      "sidebar-menu-instant-updates": "Instant Updates",
      "sidebar-menu-instant-updates-data-sets": "Data Sets",
      "sidebar-menu-instant-updates-templates": "Templates",
      "sidebar-menu-instant-updates-runner": "Scheduler",
      "sidebar-menu-instant-updates-push-notifications": "Push Notifications",
      "sidebar-menu-administration-domains": "Domains",
      "sidebar-menu-administration-support-tickets": "Support Tickets",
      "sidebar-menu-administration-system-notifications": "System Notifications",
      "sidebar-menu-administration-system-queue": "System Queue",
      "sidebar-menu-support": "Support",
      "sidebar-menu-support-my-tickets": "My Tickets",
      "sidebar-menu-support-chat": "Chat",
      "sidebar-menu-support-email": "Email",
      "sidebar-menu-anamnesis": "Anamnesis",
      "sidebar-menu-anamnesis-settings": "Settings",
      "sidebar-menu-anamnesis-knowledge-graphs": "Knowledge Graphs",
      "sidebar-menu-anamnesis-ai-prompts": "AI Prompts",
      "sidebar-menu-beamonscreen": "Beam on Screen",
      "sidebar-menu-beamonscreen-assets": "Assets",
      "sidebar-menu-beamonscreen-reel-maker": "Reel Maker",
      "sidebar-menu-beamonscreen-manage-screens": "Manage Screens",
      "sidebar-menu-beamonscreen-playlists": "Playlists",
      "sidebar-menu-infralytiqs": "Infralytiqs",
      "sidebar-menu-infralytiqs-settings": "Settings",
      "sidebar-menu-infralytiqs-reports": "Reports",
      "sidebar-menu-infralytiqs-logs": "Logs",
      "sidebar-menu-infralytiqs-audit-logs": "Audit Logs",
      "sidebar-menu-analytics": "Analytics",
      "sidebar-menu-analytics-settings": "Settings",
      "sidebar-menu-analytics-reports": "Reports",
      "sidebar-menu-analytics-audit-logs": "Audit Logs",
      "modules-instantupdate-dashboard-main-title": "Instant Update Module Dashboard",
      "modules-instantupdate-dashboard-total-templates": "Total Templates",
      "modules-instantupdate-dashboard-total-datasets": "Total Data Sets",
      "modules-instantupdate-dashboard-total-campaigns": "Total Notifications",
      "modules-instantupdate-dashboard-total-campaigns-subtitle": "Created at company level",
      "modules-instantupdate-dashboard-total-completed": "Total Completed",
      "modules-instantupdate-dashboard-total-scheduled": "Total Scheduled",
      "modules-instantupdate-dashboard-templates-creation-graph-title": "Template Creation",
      "modules-instantupdate-dashboard-datasets-creation-graph-title": "Data Sets",
      "modules-instantupdate-dashboard-campaigns-creation-graph-title": "Schedule Items",
      "modules-instantupdate-dashboard-scheduler-stats-graph-title": "Notification Stats",
      "modules-instantupdate-dashboard-whatsapp-campaign-summary-graph-title": "WhatsApp Notifications Summary",
      "modules-instantupdate-dashboard-email-campaign-summary-graph-title": "Email Notifications Summary",
      "modules-settings-whatsapp-offline-subtitle": "Schedule new WhatsApp Message to bring it 'Online'.",
      "modules-settings-whatsapp-offline-title": "WhatsApp BOT 'Offline'",
      "modules-settings-whatsapp-online-subtitle": "Your WhatsApp bot is online and ready to send notifications messages!",
      "modules-settings-whatsapp-online-title": "WhatsApp BOT 'Online'",
      "modules-settings-whatsapp-busy-subtitle": "WhatsApp Bot is currently busy. Please wait or try again later.",
      "modules-settings-whatsapp-busy-title": "WhatsApp BOT 'Busy'",
      "modules-settings-whatsapp-not-configured-subtitle": "Please contact your administrator to initiate the WhatsApp Bot.",
      "modules-settings-whatsapp-not-configured-title": "WhatsApp BOT 'Not Configured'",
      "modules-settings-whatsapp-no-qr-code-available": "No QR Code Available",
      "modules-settings-whatsapp-start-the-whatsapp-bot-to-generate-qr-code": "Start the WhatsApp bot to generate QR code",
      "modules-settings-whatsapp-qr-code-error": "QR Code Error",
      "modules-settings-whatsapp-generating-qr-code": "Generating QR Code...",
      "modules-settings-whatsapp-access-denied": "Access Denied",
      "modules-settings-whatsapp-you-dont-have-permission-to-access-whatsapp-configuration": "You don't have permission to access WhatsApp configuration.",
      "modules-settings-whatsapp-whatsapp-configuration-saved-successfully": "WhatsApp configuration saved successfully!",
      "modules-settings-whatsapp-loading-whatsapp-configuration": "Loading WhatsApp Configuration",
      "modules-settings-whatsapp-please-wait": "Please wait...",
      "modules-settings-whatsapp-fetching-latest-configuration-from-server": "Fetching latest configuration from server",
      "modules-settings-whatsapp-scan-qr-code": "Scan QR Code",
      "modules-settings-whatsapp-scan-to-connect-whatsapp-bot": "Scan to connect WhatsApp bot",
      "modules-settings-whatsapp-qr-code-is-ready-for-scanning": "QR code is ready for scanning",
      "modules-settings-whatsapp-your-whatsapp-bot-is-online-and-ready-to-send-campaign-messages": "Your WhatsApp bot is online and ready to send notifications messages!",
      "modules-settings-whatsapp-please-contact-your-administrator-to-initiate-the-whatsapp-bot": "Please contact your administrator to initiate the WhatsApp Bot.",
      "modules-instantupdate-runner-table-column-start-timestamp": "Start Timestamp",
      "modules-instantupdate-runner-table-column-status": "Status",
      "modules-instantupdate-runner-table-column-time-interval": "Time Interval",
      "modules-instantupdate-runner-table-column-created-on": "Created On",
      "modules-instantupdate-runner-table-column-campaign-name": "Notification Name",
      "modules-instantupdate-runner-table-column-campaign-type": "Notification Type",
      "modules-instantupdate-runner-table-column-template-name": "Template Name",
      "modules-instantupdate-runner-table-column-dataset-name": "Data Set Name",
      "modules-instantupdate-runner-table-column-created-by-user-name": "Created By",
      "modules-instantupdate-runner-table-column-actions": "Actions",
      "modules-instantupdate-runner-tab-scheduled": "Scheduled",
      "modules-instantupdate-runner-tab-in-progress": "In Progress",
      "modules-instantupdate-runner-tab-completed": "Completed",
      "modules-instantupdate-runner-access-denied-title": "Access Denied",
      "modules-instantupdate-runner-access-denied-subtitle": "You don't have permission to access the Scheduler.",
      "modules-instantupdate-runner-empty-title": "No Templates or Datasets",
      "modules-instantupdate-runner-empty-subtitle": "Please create at least one template and one dataset to use the Scheduler.",
      "modules-instantupdate-runner-breadcrumbs-runner": "Scheduler",
      "modules-instantupdate-runner-page-title": "Scheduler",
      "modules-instantupdate-runner-page-subtitle": "Schedule and manage your Notifications",
      "modules-instantupdate-runner-button-refresh": "Refresh",
      "modules-instantupdate-runner-button-new-campaign": "New Notification",
      "modules-instantupdate-runner-action-view-report": "View Report",
      "modules-instantupdate-runner-action-edit": "Edit Notification",
      "modules-instantupdate-runner-action-delete": "Delete Notification",
      "modules-instantupdate-runner-action-stop": "Stop Notification",
      "modules-instantupdate-runner-status-scheduled": "Scheduled",
      "modules-instantupdate-runner-status-running": "Running",
      "modules-instantupdate-runner-status-queued": "Queued",
      "modules-instantupdate-runner-status-completed": "Completed",
      "modules-instantupdate-runner-status-cancelled": "Cancelled",
      "modules-instantupdate-runner-status-stopped": "Stopped",
      "modules-instantupdate-runner-status-blocked": "Blocked",
      "modules-instantupdate-runner-unknown-template": "Unknown Template",
      "modules-instantupdate-runner-unknown-dataset": "Unknown Dataset",
      "modules-instantupdate-runner-unknown-user": "Unknown User",
      "modules-instantupdate-runner-steps-select-template": "Select Template",
      "modules-instantupdate-runner-steps-select-dataset": "Select DataSet",
      "modules-instantupdate-runner-steps-configure-conditions": "Configure Conditions",
      "modules-instantupdate-runner-steps-set-interval": "Set Interval",
      "modules-instantupdate-runner-steps-schedule": "Schedule Notification",
      "modules-instantupdate-runner-select-template-title": "Select Template",
      "modules-instantupdate-runner-select-template-subtitle": "Choose a template for your Notification message",
      "modules-instantupdate-runner-select-template-label": "Template *",
      "modules-instantupdate-runner-select-template-helper": "Select a template to continue",
      "modules-instantupdate-runner-wa-telegram-risk-panel-title": "Use at your own RISK",
      "modules-instantupdate-runner-wa-telegram-risk-warning": "Notifications of type WhatsApp / Telegram must be used on your own risk, as we only recommend using these type of notifications for your KNOWN WhatsApp / Telegram contacts - with 60+ seconds interval between messages in Step 4. Failing to do so or sending messages to UNKNOWN contacts may or will lead to suspension of your WhatsApp / Telegram Account by Meta / Telegram for 24 hours, AND we will NOT take responsibility for getting this UNBLOCKED. Please use this at your own risk with UNKNOWN Contacts.\nFor information, contact support@instantupdate.ai",
      "modules-instantupdate-runner-select-dataset-title": "Select Dataset",
      "modules-instantupdate-runner-select-dataset-subtitle": "Choose the dataset containing your target audience",
      "modules-instantupdate-runner-select-dataset-label": "Dataset *",
      "modules-instantupdate-runner-select-dataset-helper": "Select a dataset to continue",
      "modules-instantupdate-runner-configure-conditions-title": "Configure Notification Conditions",
      "modules-instantupdate-runner-consent-requirements": "Consent Requirements",
      "modules-instantupdate-runner-target-audience": "Target Audience",
      "modules-instantupdate-runner-target-audience-all": "Send to all contacts",
      "modules-instantupdate-runner-target-audience-consent": "Send to contacts with consent",
      "modules-instantupdate-runner-target-audience-my-contacts": "Send to my contacts only",
      "modules-instantupdate-runner-target-audience-my-contacts-consent": "Send to my contacts only with consent",
      "modules-instantupdate-runner-filtering-conditions-title": "Filtering Conditions",
      "modules-instantupdate-runner-condition-logic-label": "Condition Logic",
      "modules-instantupdate-runner-condition-logic-and": "AND (All conditions must be met)",
      "modules-instantupdate-runner-condition-logic-or": "OR (Any condition can be met)",
      "modules-instantupdate-runner-condition-column-label": "Column",
      "modules-instantupdate-runner-condition-operator-label": "Operator",
      "modules-instantupdate-runner-condition-operator-equals": "Equal to",
      "modules-instantupdate-runner-condition-operator-not-equals": "Not Equal to",
      "modules-instantupdate-runner-condition-operator-less-than": "Less than",
      "modules-instantupdate-runner-condition-operator-less-than-equal": "Less than or equal to",
      "modules-instantupdate-runner-condition-operator-greater-than": "Greater than",
      "modules-instantupdate-runner-condition-operator-greater-than-equal": "Greater than or equal to",
      "modules-instantupdate-runner-condition-operator-contains": "Contains",
      "modules-instantupdate-runner-condition-value-label": "Value",
      "modules-instantupdate-runner-button-add-condition": "Add Condition",
      "modules-instantupdate-runner-select-dataset-prompt": "Please select a dataset first to configure conditions.",
      "modules-instantupdate-runner-set-interval-title": "Set Time Interval",
      "modules-instantupdate-runner-set-interval-subtitle": "Configure the delay between notifications messages",
      "modules-instantupdate-runner-time-interval-label": "Time Interval (seconds) *",
      "modules-instantupdate-runner-time-interval-helper-wa-min": "For WhatsApp, recommended minimum is 60 seconds.",
      "modules-instantupdate-runner-time-interval-helper-general": "Enter time interval between notifications messages (minimum 1 second)",
      "modules-instantupdate-runner-seconds-suffix": "seconds",
      "modules-instantupdate-runner-schedule-title": "Schedule Notification",
      "modules-instantupdate-runner-schedule-subtitle": "Set notification details and schedule",
      "modules-instantupdate-runner-campaign-name-label": "Notification Name *",
      "modules-instantupdate-runner-campaign-name-helper": "Enter a name for your notification",
      "modules-instantupdate-runner-description-label": "Description (Optional)",
      "modules-instantupdate-runner-start-timestamp-label": "Start Timestamp *",
      "modules-instantupdate-runner-start-timestamp-helper": "Select when the notification should start",
      "modules-instantupdate-runner-mobile-step-label": "Step",
      "modules-instantupdate-runner-mobile-step-of": "of",
      "modules-instantupdate-runner-button-cancel": "Cancel",
      "modules-instantupdate-runner-button-back": "Back",
      "modules-instantupdate-runner-button-update": "Update Notification",
      "modules-instantupdate-runner-button-submit": "Submit",
      "modules-instantupdate-runner-button-next": "Next",
      "modules-instantupdate-runner-snackbar-updated": "Notification updated successfully!",
      "modules-instantupdate-runner-snackbar-created": "Notification created successfully!",
      "modules-instantupdate-runner-snackbar-deleted": "Notification deleted successfully!",
      "modules-instantupdate-runner-snackbar-stopped": "Notification stopped successfully!",
      "modules-instantupdate-runner-delete-dialog-title": "Confirm Deletion",
      "modules-instantupdate-runner-delete-dialog-description-prefix": "Are you sure you want to delete notification",
      "modules-instantupdate-runner-delete-dialog-button-delete": "Delete",
      "modules-instantupdate-runner-stop-dialog-title": "Confirm Stop Notification",
      "modules-instantupdate-runner-stop-dialog-description-prefix": "Are you sure you want to stop notification",
      "modules-instantupdate-runner-stop-dialog-description-suffix": "This action cannot be undone. Once stopped, this notification cannot be restarted and you will need to start a new notification if you wish to continue.",
      "modules-instantupdate-runner-stop-dialog-button-stop": "Stop Notification",
      "modules-instantupdate-runner-report-campaign-information-title": "Notification Information",
      "modules-instantupdate-runner-report-breadcrumb-runner": "Scheduler",
      "modules-instantupdate-runner-report-breadcrumb-report": "Notification Report",
      "modules-instantupdate-runner-new-dialogbox-create-title": "Create New Notification",
      "modules-instantupdate-runner-new-dialogbox-edit-title": "Edit Notification",
    },
    fr: {
      
    },
  },

  //PROD
  "my.campaignking.ai": {
    en: {
      "sidebar-menu-dashboard": "Dashboard",
      "sidebar-menu-settings": "Settings",
      "sidebar-menu-settings-profile": "Profile",
      "sidebar-menu-settings-security": "Security",
      "sidebar-menu-settings-notifications": "Notifications",
      "sidebar-menu-settings-users": "Users",
      "sidebar-menu-settings-companies": "Companies",
      "sidebar-menu-settings-whatsapp": "WhatsApp",
      "sidebar-menu-settings-whatsappbusiness": "WhatsApp Business",
      "sidebar-menu-settings-telegram": "Telegram",
      "sidebar-menu-settings-sms": "GSM Text",
      "sidebar-menu-settings-email": "Email",
      "sidebar-menu-settings-bot-manager": "BOT Manager",
      "sidebar-menu-settings-openai": "OpenAI",
      "sidebar-menu-settings-youtube": "YouTube",
      "sidebar-menu-settings-instagram": "Instagram",
      "sidebar-menu-settings-tiktok": "TikTok",
      "sidebar-menu-settings-signal": "Signal",
      "sidebar-menu-instant-updates": "Campaigns",
      "sidebar-menu-instant-updates-data-sets": "Data Sets",
      "sidebar-menu-instant-updates-templates": "Templates",
      "sidebar-menu-instant-updates-runner": "Runner",
      "sidebar-menu-instant-updates-activities": "Activities",
      "sidebar-menu-administration-domains": "Domains",
      "sidebar-menu-administration-support-tickets": "Support Tickets",
      "sidebar-menu-administration-system-notifications": "System Notifications",
      "sidebar-menu-administration-system-queue": "System Queue",
      "sidebar-menu-support": "Support",
      "sidebar-menu-support-my-tickets": "My Tickets",
      "sidebar-menu-support-chat": "Chat",
      "sidebar-menu-support-email": "Email",
      "sidebar-menu-anamnesis": "Anamnesis",
      "sidebar-menu-anamnesis-settings": "Settings",
      "sidebar-menu-anamnesis-knowledge-graphs": "Knowledge Graphs",
      "sidebar-menu-anamnesis-ai-prompts": "AI Prompts",
      "sidebar-menu-beamonscreen": "Beam on Screen",
      "sidebar-menu-beamonscreen-assets": "Assets",
      "sidebar-menu-beamonscreen-reel-maker": "Reel Maker",
      "sidebar-menu-beamonscreen-manage-screens": "Manage Screens",
      "sidebar-menu-beamonscreen-playlists": "Playlists",
      "sidebar-menu-infralytiqs": "Infralytiqs",
      "sidebar-menu-infralytiqs-settings": "Settings",
      "sidebar-menu-infralytiqs-reports": "Reports",
      "sidebar-menu-infralytiqs-logs": "Logs",
      "sidebar-menu-infralytiqs-audit-logs": "Audit Logs",
      "sidebar-menu-analytics": "Analytics",
      "sidebar-menu-analytics-settings": "Settings",
      "sidebar-menu-analytics-reports": "Reports",
      "sidebar-menu-analytics-audit-logs": "Audit Logs",
      "modules-instantupdate-dashboard-main-title": "Campaigns Module Dashboard",
      "modules-instantupdate-dashboard-total-templates": "Total Templates",
      "modules-instantupdate-dashboard-total-datasets": "Total Data Sets",
      "modules-instantupdate-dashboard-total-campaigns": "Total Campaigns",
      "modules-instantupdate-dashboard-total-campaigns-subtitle": "Created at company level",
      "modules-instantupdate-dashboard-total-completed": "Total Completed",
      "modules-instantupdate-dashboard-total-scheduled": "Total Scheduled",
      "modules-instantupdate-dashboard-templates-creation-graph-title": "Template Creation",
      "modules-instantupdate-dashboard-datasets-creation-graph-title": "Data Sets",
      "modules-instantupdate-dashboard-campaigns-creation-graph-title": "Schedule Campaigns",
      "modules-instantupdate-dashboard-scheduler-stats-graph-title": "Campaign Stats",
      "modules-instantupdate-dashboard-whatsapp-campaign-summary-graph-title": "WhatsApp Campaign Summary",
      "modules-instantupdate-dashboard-email-campaign-summary-graph-title": "Email Campaign Summary",
      "modules-settings-whatsapp-offline-subtitle": "Schedule new WhatsApp Message to bring it 'Online'.",
      "modules-settings-whatsapp-offline-title": "WhatsApp BOT 'Offline'",
      "modules-settings-whatsapp-online-subtitle": "Your WhatsApp bot is online and ready to send notifications messages!",
      "modules-settings-whatsapp-online-title": "WhatsApp BOT 'Online'",
      "modules-settings-whatsapp-busy-subtitle": "WhatsApp Bot is currently busy. Please wait or try again later.",
      "modules-settings-whatsapp-busy-title": "WhatsApp BOT 'Busy'",
      "modules-settings-whatsapp-not-configured-subtitle": "Please contact your administrator to initiate the WhatsApp Bot.",
      "modules-settings-whatsapp-not-configured-title": "WhatsApp BOT 'Not Configured'",
      "modules-settings-whatsapp-no-qr-code-available": "No QR Code Available",
      "modules-settings-whatsapp-start-the-whatsapp-bot-to-generate-qr-code": "Start the WhatsApp bot to generate QR code",
      "modules-settings-whatsapp-qr-code-error": "QR Code Error",
      "modules-settings-whatsapp-generating-qr-code": "Generating QR Code...",
      "modules-settings-whatsapp-access-denied": "Access Denied",
      "modules-settings-whatsapp-you-dont-have-permission-to-access-whatsapp-configuration": "You don't have permission to access WhatsApp configuration.",
      "modules-settings-whatsapp-whatsapp-configuration-saved-successfully": "WhatsApp configuration saved successfully!",
      "modules-settings-whatsapp-loading-whatsapp-configuration": "Loading WhatsApp Configuration",
      "modules-settings-whatsapp-please-wait": "Please wait...",
      "modules-settings-whatsapp-fetching-latest-configuration-from-server": "Fetching latest configuration from server",
      "modules-settings-whatsapp-scan-qr-code": "Scan QR Code",
      "modules-settings-whatsapp-scan-to-connect-whatsapp-bot": "Scan to connect WhatsApp bot",
      "modules-settings-whatsapp-qr-code-is-ready-for-scanning": "QR code is ready for scanning",
      "modules-settings-whatsapp-your-whatsapp-bot-is-online-and-ready-to-send-campaign-messages": "Your WhatsApp bot is online and ready to send notifications messages!",
      "modules-settings-whatsapp-please-contact-your-administrator-to-initiate-the-whatsapp-bot": "Please contact your administrator to initiate the WhatsApp Bot.",
      "modules-instantupdate-runner-access-denied-title": "Access Denied",
      "modules-instantupdate-runner-access-denied-subtitle": "You don't have permission to access the Campaign Runner.",
      "modules-instantupdate-runner-empty-title": "No Templates or Datasets",
      "modules-instantupdate-runner-empty-subtitle": "Please create at least one template and one dataset to use the Campaign Runner.",
      "modules-instantupdate-runner-breadcrumbs-runner": "Runner",
      "modules-instantupdate-runner-page-title": "Campaign Runner",
      "modules-instantupdate-runner-page-subtitle": "Schedule and manage your marketing campaigns",
      "modules-instantupdate-runner-button-refresh": "Refresh",
      "modules-instantupdate-runner-button-new-campaign": "New Campaign",
      "modules-instantupdate-runner-action-view-report": "View Report",
      "modules-instantupdate-runner-action-edit": "Edit Campaign",
      "modules-instantupdate-runner-action-delete": "Delete Campaign",
      "modules-instantupdate-runner-action-stop": "Stop Campaign",
      "modules-instantupdate-runner-status-scheduled": "Scheduled",
      "modules-instantupdate-runner-status-running": "Running",
      "modules-instantupdate-runner-status-queued": "Queued",
      "modules-instantupdate-runner-status-completed": "Completed",
      "modules-instantupdate-runner-status-cancelled": "Cancelled",
      "modules-instantupdate-runner-status-stopped": "Stopped",
      "modules-instantupdate-runner-status-blocked": "Blocked",
      "modules-instantupdate-runner-unknown-template": "Unknown Template",
      "modules-instantupdate-runner-unknown-dataset": "Unknown Dataset",
      "modules-instantupdate-runner-unknown-user": "Unknown User",
      "modules-instantupdate-runner-steps-select-template": "Select Template",
      "modules-instantupdate-runner-steps-select-dataset": "Select DataSet",
      "modules-instantupdate-runner-steps-configure-conditions": "Configure Conditions",
      "modules-instantupdate-runner-steps-set-interval": "Set Interval",
      "modules-instantupdate-runner-steps-schedule": "Schedule Campaign",
      "modules-instantupdate-runner-select-template-title": "Select Template",
      "modules-instantupdate-runner-select-template-subtitle": "Choose a template for your campaign message",
      "modules-instantupdate-runner-select-template-label": "Template *",
      "modules-instantupdate-runner-select-template-helper": "Select a template to continue",
      "modules-instantupdate-runner-wa-telegram-risk-panel-title": "Use at your own RISK",
      "modules-instantupdate-runner-wa-telegram-risk-warning": "Campaigns of type WhatsApp / Telegram must be used on your own risk, as we only recommend using these type of campaign for your KNOWN WhatsApp / Telegram contacts - with 60+ seconds interval between messages in Step 4. Failing to do so or sending messages to UNKNOWN contacts may or will lead to suspension of your WhatsApp / Telegram Account by Meta / Telegram for 24 hours, AND we will NOT take responsibility for getting this UNBLOCKED. Please use this at your own risk with UNKNOWN Contacts.\nFor information, contact support@campaignking.ai",
      "modules-instantupdate-runner-select-dataset-title": "Select Dataset",
      "modules-instantupdate-runner-select-dataset-subtitle": "Choose the dataset containing your target audience",
      "modules-instantupdate-runner-select-dataset-label": "Dataset *",
      "modules-instantupdate-runner-select-dataset-helper": "Select a dataset to continue",
      "modules-instantupdate-runner-configure-conditions-title": "Configure Campaign Conditions",
      "modules-instantupdate-runner-consent-requirements": "Consent Requirements",
      "modules-instantupdate-runner-target-audience": "Target Audience",
      "modules-instantupdate-runner-target-audience-all": "Send to all contacts",
      "modules-instantupdate-runner-target-audience-consent": "Send to contacts with consent",
      "modules-instantupdate-runner-target-audience-my-contacts": "Send to my contacts only",
      "modules-instantupdate-runner-target-audience-my-contacts-consent": "Send to my contacts only with consent",
      "modules-instantupdate-runner-filtering-conditions-title": "Filtering Conditions",
      "modules-instantupdate-runner-condition-logic-label": "Condition Logic",
      "modules-instantupdate-runner-condition-logic-and": "AND (All conditions must be met)",
      "modules-instantupdate-runner-condition-logic-or": "OR (Any condition can be met)",
      "modules-instantupdate-runner-condition-column-label": "Column",
      "modules-instantupdate-runner-condition-operator-label": "Operator",
      "modules-instantupdate-runner-condition-operator-equals": "Equal to",
      "modules-instantupdate-runner-condition-operator-not-equals": "Not Equal to",
      "modules-instantupdate-runner-condition-operator-less-than": "Less than",
      "modules-instantupdate-runner-condition-operator-less-than-equal": "Less than or equal to",
      "modules-instantupdate-runner-condition-operator-greater-than": "Greater than",
      "modules-instantupdate-runner-condition-operator-greater-than-equal": "Greater than or equal to",
      "modules-instantupdate-runner-condition-operator-contains": "Contains",
      "modules-instantupdate-runner-condition-value-label": "Value",
      "modules-instantupdate-runner-button-add-condition": "Add Condition",
      "modules-instantupdate-runner-select-dataset-prompt": "Please select a dataset first to configure conditions.",
      "modules-instantupdate-runner-set-interval-title": "Set Time Interval",
      "modules-instantupdate-runner-set-interval-subtitle": "Configure the delay between campaign messages",
      "modules-instantupdate-runner-time-interval-label": "Time Interval (seconds) *",
      "modules-instantupdate-runner-time-interval-helper-wa-min": "For WhatsApp, recommended minimum is 60 seconds.",
      "modules-instantupdate-runner-time-interval-helper-general": "Enter time interval between campaign messages (minimum 1 second)",
      "modules-instantupdate-runner-seconds-suffix": "seconds",
      "modules-instantupdate-runner-schedule-title": "Schedule Campaign",
      "modules-instantupdate-runner-schedule-subtitle": "Set campaign details and schedule",
      "modules-instantupdate-runner-campaign-name-label": "Campaign Name *",
      "modules-instantupdate-runner-campaign-name-helper": "Enter a name for your campaign",
      "modules-instantupdate-runner-description-label": "Description (Optional)",
      "modules-instantupdate-runner-start-timestamp-label": "Start Timestamp *",
      "modules-instantupdate-runner-start-timestamp-helper": "Select when the campaign should start",
      "modules-instantupdate-runner-mobile-step-label": "Step",
      "modules-instantupdate-runner-mobile-step-of": "of",
      "modules-instantupdate-runner-button-cancel": "Cancel",
      "modules-instantupdate-runner-button-back": "Back",
      "modules-instantupdate-runner-button-update": "Update Campaign",
      "modules-instantupdate-runner-button-submit": "Submit",
      "modules-instantupdate-runner-button-next": "Next",
      "modules-instantupdate-runner-snackbar-updated": "Campaign updated successfully!",
      "modules-instantupdate-runner-snackbar-created": "Campaign created successfully!",
      "modules-instantupdate-runner-snackbar-deleted": "Campaign deleted successfully!",
      "modules-instantupdate-runner-snackbar-stopped": "Campaign stopped successfully!",
      "modules-instantupdate-runner-delete-dialog-title": "Confirm Deletion",
      "modules-instantupdate-runner-delete-dialog-description-prefix": "Are you sure you want to delete campaign",
      "modules-instantupdate-runner-delete-dialog-button-delete": "Delete",
      "modules-instantupdate-runner-stop-dialog-title": "Confirm Stop Campaign",
      "modules-instantupdate-runner-stop-dialog-description-prefix": "Are you sure you want to stop campaign",
      "modules-instantupdate-runner-stop-dialog-description-suffix": "This action cannot be undone. Once stopped, this campaign cannot be restarted and you will need to start a new campaign if you wish to continue.",
      "modules-instantupdate-runner-stop-dialog-button-stop": "Stop Campaign",
      "modules-instantupdate-runner-report-campaign-information-title": "Campaign Information",
      "modules-instantupdate-runner-report-breadcrumb-runner": "Runner",
      "modules-instantupdate-runner-report-breadcrumb-report": "Campaign Report",
      "modules-instantupdate-runner-new-dialogbox-create-title": "Create New Campaign",
      "modules-instantupdate-runner-new-dialogbox-edit-title": "Edit Campaign",
      "modules-instantupdate-runner-table-column-start-timestamp": "Start Timestamp",
      "modules-instantupdate-runner-table-column-status": "Status",
      "modules-instantupdate-runner-table-column-time-interval": "Time Interval",
      "modules-instantupdate-runner-table-column-created-on": "Created On",
      "modules-instantupdate-runner-table-column-campaign-name": "Campaign Name",
      "modules-instantupdate-runner-table-column-campaign-type": "Campaign Type",
      "modules-instantupdate-runner-table-column-template-name": "Template Name",
      "modules-instantupdate-runner-table-column-dataset-name": "Data Set Name",
      "modules-instantupdate-runner-table-column-created-by-user-name": "Created By",
      "modules-instantupdate-runner-table-column-actions": "Actions",
      "modules-instantupdate-runner-tab-scheduled": "Scheduled",
      "modules-instantupdate-runner-tab-in-progress": "In Progress",
      "modules-instantupdate-runner-tab-completed": "Completed",
    },
    fr: {
      
    },
  },
  "my.instantupdate.ai": {
    en: {
      "sidebar-menu-dashboard": "Dashboard",
      "sidebar-menu-settings": "Settings",
      "sidebar-menu-settings-profile": "Profile",
      "sidebar-menu-settings-security": "Security",
      "sidebar-menu-settings-notifications": "Notifications",
      "sidebar-menu-settings-users": "Users",
      "sidebar-menu-settings-companies": "Companies",
      "sidebar-menu-settings-whatsapp": "WhatsApp",
      "sidebar-menu-settings-whatsappbusiness": "WhatsApp Business",
      "sidebar-menu-settings-telegram": "Telegram",
      "sidebar-menu-settings-sms": "GSM Text",
      "sidebar-menu-settings-email": "Email",
      "sidebar-menu-settings-bot-manager": "BOT Manager",
      "sidebar-menu-settings-openai": "OpenAI",
      "sidebar-menu-settings-youtube": "YouTube",
      "sidebar-menu-settings-instagram": "Instagram",
      "sidebar-menu-settings-tiktok": "TikTok",
      "sidebar-menu-settings-signal": "Signal",
      "sidebar-menu-instant-updates": "Instant Updates",
      "sidebar-menu-instant-updates-data-sets": "Data Sets",
      "sidebar-menu-instant-updates-templates": "Templates",
      "sidebar-menu-instant-updates-runner": "Scheduler",
      "sidebar-menu-instant-updates-push-notifications": "Push Notifications",
      "sidebar-menu-administration-domains": "Domains",
      "sidebar-menu-administration-support-tickets": "Support Tickets",
      "sidebar-menu-administration-system-notifications": "System Notifications",
      "sidebar-menu-administration-system-queue": "System Queue",
      "sidebar-menu-support": "Support",
      "sidebar-menu-support-my-tickets": "My Tickets",
      "sidebar-menu-support-chat": "Chat",
      "sidebar-menu-support-email": "Email",
      "sidebar-menu-anamnesis": "Anamnesis",
      "sidebar-menu-anamnesis-settings": "Settings",
      "sidebar-menu-anamnesis-knowledge-graphs": "Knowledge Graphs",
      "sidebar-menu-anamnesis-ai-prompts": "AI Prompts",
      "sidebar-menu-beamonscreen": "Beam on Screen",
      "sidebar-menu-beamonscreen-assets": "Assets",
      "sidebar-menu-beamonscreen-reel-maker": "Reel Maker",
      "sidebar-menu-beamonscreen-manage-screens": "Manage Screens",
      "sidebar-menu-beamonscreen-playlists": "Playlists",
      "sidebar-menu-infralytiqs": "Infralytiqs",
      "sidebar-menu-infralytiqs-settings": "Settings",
      "sidebar-menu-infralytiqs-reports": "Reports",
      "sidebar-menu-infralytiqs-logs": "Logs",
      "sidebar-menu-infralytiqs-audit-logs": "Audit Logs",
      "sidebar-menu-analytics": "Analytics",
      "sidebar-menu-analytics-settings": "Settings",
      "sidebar-menu-analytics-reports": "Reports",
      "sidebar-menu-analytics-audit-logs": "Audit Logs",
      "modules-instantupdate-dashboard-main-title": "Instant Update Module Dashboard",
      "modules-instantupdate-dashboard-total-templates": "Total Templates",
      "modules-instantupdate-dashboard-total-datasets": "Total Data Sets",
      "modules-instantupdate-dashboard-total-campaigns": "Total Notifications",
      "modules-instantupdate-dashboard-total-campaigns-subtitle": "Created at company level",
      "modules-instantupdate-dashboard-total-completed": "Total Completed",
      "modules-instantupdate-dashboard-total-scheduled": "Total Scheduled",
      "modules-instantupdate-dashboard-templates-creation-graph-title": "Template Creation",
      "modules-instantupdate-dashboard-datasets-creation-graph-title": "Data Sets",
      "modules-instantupdate-dashboard-campaigns-creation-graph-title": "Schedule Items",
      "modules-instantupdate-dashboard-scheduler-stats-graph-title": "Notification Stats",
      "modules-instantupdate-dashboard-whatsapp-campaign-summary-graph-title": "WhatsApp Notifications Summary",
      "modules-instantupdate-dashboard-email-campaign-summary-graph-title": "Email Notifications Summary",
      "modules-settings-whatsapp-offline-subtitle": "Schedule new WhatsApp Message to bring it 'Online'.",
      "modules-settings-whatsapp-offline-title": "WhatsApp BOT 'Offline'",
      "modules-settings-whatsapp-online-subtitle": "Your WhatsApp bot is online and ready to send notifications messages!",
      "modules-settings-whatsapp-online-title": "WhatsApp BOT 'Online'",
      "modules-settings-whatsapp-busy-subtitle": "WhatsApp Bot is currently busy. Please wait or try again later.",
      "modules-settings-whatsapp-busy-title": "WhatsApp BOT 'Busy'",
      "modules-settings-whatsapp-not-configured-subtitle": "Please contact your administrator to initiate the WhatsApp Bot.",
      "modules-settings-whatsapp-not-configured-title": "WhatsApp BOT 'Not Configured'",
      "modules-settings-whatsapp-no-qr-code-available": "No QR Code Available",
      "modules-settings-whatsapp-start-the-whatsapp-bot-to-generate-qr-code": "Start the WhatsApp bot to generate QR code",
      "modules-settings-whatsapp-qr-code-error": "QR Code Error",
      "modules-settings-whatsapp-generating-qr-code": "Generating QR Code...",
      "modules-settings-whatsapp-access-denied": "Access Denied",
      "modules-settings-whatsapp-you-dont-have-permission-to-access-whatsapp-configuration": "You don't have permission to access WhatsApp configuration.",
      "modules-settings-whatsapp-whatsapp-configuration-saved-successfully": "WhatsApp configuration saved successfully!",
      "modules-settings-whatsapp-loading-whatsapp-configuration": "Loading WhatsApp Configuration",
      "modules-settings-whatsapp-please-wait": "Please wait...",
      "modules-settings-whatsapp-fetching-latest-configuration-from-server": "Fetching latest configuration from server",
      "modules-settings-whatsapp-scan-qr-code": "Scan QR Code",
      "modules-settings-whatsapp-scan-to-connect-whatsapp-bot": "Scan to connect WhatsApp bot",
      "modules-settings-whatsapp-qr-code-is-ready-for-scanning": "QR code is ready for scanning",
      "modules-settings-whatsapp-your-whatsapp-bot-is-online-and-ready-to-send-campaign-messages": "Your WhatsApp bot is online and ready to send notifications messages!",
      "modules-settings-whatsapp-please-contact-your-administrator-to-initiate-the-whatsapp-bot": "Please contact your administrator to initiate the WhatsApp Bot.",
      "modules-instantupdate-runner-table-column-start-timestamp": "Start Timestamp",
      "modules-instantupdate-runner-table-column-status": "Status",
      "modules-instantupdate-runner-table-column-time-interval": "Time Interval",
      "modules-instantupdate-runner-table-column-created-on": "Created On",
      "modules-instantupdate-runner-table-column-campaign-name": "Notification Name",
      "modules-instantupdate-runner-table-column-campaign-type": "Notification Type",
      "modules-instantupdate-runner-table-column-template-name": "Template Name",
      "modules-instantupdate-runner-table-column-dataset-name": "Data Set Name",
      "modules-instantupdate-runner-table-column-created-by-user-name": "Created By",
      "modules-instantupdate-runner-table-column-actions": "Actions",
      "modules-instantupdate-runner-tab-scheduled": "Scheduled",
      "modules-instantupdate-runner-tab-in-progress": "In Progress",
      "modules-instantupdate-runner-tab-completed": "Completed",
      "modules-instantupdate-runner-access-denied-title": "Access Denied",
      "modules-instantupdate-runner-access-denied-subtitle": "You don't have permission to access the Scheduler.",
      "modules-instantupdate-runner-empty-title": "No Templates or Datasets",
      "modules-instantupdate-runner-empty-subtitle": "Please create at least one template and one dataset to use the Scheduler.",
      "modules-instantupdate-runner-breadcrumbs-runner": "Scheduler",
      "modules-instantupdate-runner-page-title": "Scheduler",
      "modules-instantupdate-runner-page-subtitle": "Schedule and manage your Notifications",
      "modules-instantupdate-runner-button-refresh": "Refresh",
      "modules-instantupdate-runner-button-new-campaign": "New Notification",
      "modules-instantupdate-runner-action-view-report": "View Report",
      "modules-instantupdate-runner-action-edit": "Edit Notification",
      "modules-instantupdate-runner-action-delete": "Delete Notification",
      "modules-instantupdate-runner-action-stop": "Stop Notification",
      "modules-instantupdate-runner-status-scheduled": "Scheduled",
      "modules-instantupdate-runner-status-running": "Running",
      "modules-instantupdate-runner-status-queued": "Queued",
      "modules-instantupdate-runner-status-completed": "Completed",
      "modules-instantupdate-runner-status-cancelled": "Cancelled",
      "modules-instantupdate-runner-status-stopped": "Stopped",
      "modules-instantupdate-runner-status-blocked": "Blocked",
      "modules-instantupdate-runner-unknown-template": "Unknown Template",
      "modules-instantupdate-runner-unknown-dataset": "Unknown Dataset",
      "modules-instantupdate-runner-unknown-user": "Unknown User",
      "modules-instantupdate-runner-steps-select-template": "Select Template",
      "modules-instantupdate-runner-steps-select-dataset": "Select DataSet",
      "modules-instantupdate-runner-steps-configure-conditions": "Configure Conditions",
      "modules-instantupdate-runner-steps-set-interval": "Set Interval",
      "modules-instantupdate-runner-steps-schedule": "Schedule Notification",
      "modules-instantupdate-runner-select-template-title": "Select Template",
      "modules-instantupdate-runner-select-template-subtitle": "Choose a template for your Notification message",
      "modules-instantupdate-runner-select-template-label": "Template *",
      "modules-instantupdate-runner-select-template-helper": "Select a template to continue",
      "modules-instantupdate-runner-wa-telegram-risk-panel-title": "Use at your own RISK",
      "modules-instantupdate-runner-wa-telegram-risk-warning": "Notifications of type WhatsApp / Telegram must be used on your own risk, as we only recommend using these type of notifications for your KNOWN WhatsApp / Telegram contacts - with 60+ seconds interval between messages in Step 4. Failing to do so or sending messages to UNKNOWN contacts may or will lead to suspension of your WhatsApp / Telegram Account by Meta / Telegram for 24 hours, AND we will NOT take responsibility for getting this UNBLOCKED. Please use this at your own risk with UNKNOWN Contacts.\nFor information, contact support@instantupdate.ai",
      "modules-instantupdate-runner-select-dataset-title": "Select Dataset",
      "modules-instantupdate-runner-select-dataset-subtitle": "Choose the dataset containing your target audience",
      "modules-instantupdate-runner-select-dataset-label": "Dataset *",
      "modules-instantupdate-runner-select-dataset-helper": "Select a dataset to continue",
      "modules-instantupdate-runner-configure-conditions-title": "Configure Notification Conditions",
      "modules-instantupdate-runner-consent-requirements": "Consent Requirements",
      "modules-instantupdate-runner-target-audience": "Target Audience",
      "modules-instantupdate-runner-target-audience-all": "Send to all contacts",
      "modules-instantupdate-runner-target-audience-consent": "Send to contacts with consent",
      "modules-instantupdate-runner-target-audience-my-contacts": "Send to my contacts only",
      "modules-instantupdate-runner-target-audience-my-contacts-consent": "Send to my contacts only with consent",
      "modules-instantupdate-runner-filtering-conditions-title": "Filtering Conditions",
      "modules-instantupdate-runner-condition-logic-label": "Condition Logic",
      "modules-instantupdate-runner-condition-logic-and": "AND (All conditions must be met)",
      "modules-instantupdate-runner-condition-logic-or": "OR (Any condition can be met)",
      "modules-instantupdate-runner-condition-column-label": "Column",
      "modules-instantupdate-runner-condition-operator-label": "Operator",
      "modules-instantupdate-runner-condition-operator-equals": "Equal to",
      "modules-instantupdate-runner-condition-operator-not-equals": "Not Equal to",
      "modules-instantupdate-runner-condition-operator-less-than": "Less than",
      "modules-instantupdate-runner-condition-operator-less-than-equal": "Less than or equal to",
      "modules-instantupdate-runner-condition-operator-greater-than": "Greater than",
      "modules-instantupdate-runner-condition-operator-greater-than-equal": "Greater than or equal to",
      "modules-instantupdate-runner-condition-operator-contains": "Contains",
      "modules-instantupdate-runner-condition-value-label": "Value",
      "modules-instantupdate-runner-button-add-condition": "Add Condition",
      "modules-instantupdate-runner-select-dataset-prompt": "Please select a dataset first to configure conditions.",
      "modules-instantupdate-runner-set-interval-title": "Set Time Interval",
      "modules-instantupdate-runner-set-interval-subtitle": "Configure the delay between notifications messages",
      "modules-instantupdate-runner-time-interval-label": "Time Interval (seconds) *",
      "modules-instantupdate-runner-time-interval-helper-wa-min": "For WhatsApp, recommended minimum is 60 seconds.",
      "modules-instantupdate-runner-time-interval-helper-general": "Enter time interval between notifications messages (minimum 1 second)",
      "modules-instantupdate-runner-seconds-suffix": "seconds",
      "modules-instantupdate-runner-schedule-title": "Schedule Notification",
      "modules-instantupdate-runner-schedule-subtitle": "Set notification details and schedule",
      "modules-instantupdate-runner-campaign-name-label": "Notification Name *",
      "modules-instantupdate-runner-campaign-name-helper": "Enter a name for your notification",
      "modules-instantupdate-runner-description-label": "Description (Optional)",
      "modules-instantupdate-runner-start-timestamp-label": "Start Timestamp *",
      "modules-instantupdate-runner-start-timestamp-helper": "Select when the notification should start",
      "modules-instantupdate-runner-mobile-step-label": "Step",
      "modules-instantupdate-runner-mobile-step-of": "of",
      "modules-instantupdate-runner-button-cancel": "Cancel",
      "modules-instantupdate-runner-button-back": "Back",
      "modules-instantupdate-runner-button-update": "Update Notification",
      "modules-instantupdate-runner-button-submit": "Submit",
      "modules-instantupdate-runner-button-next": "Next",
      "modules-instantupdate-runner-snackbar-updated": "Notification updated successfully!",
      "modules-instantupdate-runner-snackbar-created": "Notification created successfully!",
      "modules-instantupdate-runner-snackbar-deleted": "Notification deleted successfully!",
      "modules-instantupdate-runner-snackbar-stopped": "Notification stopped successfully!",
      "modules-instantupdate-runner-delete-dialog-title": "Confirm Deletion",
      "modules-instantupdate-runner-delete-dialog-description-prefix": "Are you sure you want to delete notification",
      "modules-instantupdate-runner-delete-dialog-button-delete": "Delete",
      "modules-instantupdate-runner-stop-dialog-title": "Confirm Stop Notification",
      "modules-instantupdate-runner-stop-dialog-description-prefix": "Are you sure you want to stop notification",
      "modules-instantupdate-runner-stop-dialog-description-suffix": "This action cannot be undone. Once stopped, this notification cannot be restarted and you will need to start a new notification if you wish to continue.",
      "modules-instantupdate-runner-stop-dialog-button-stop": "Stop Notification",
      "modules-instantupdate-runner-report-campaign-information-title": "Notification Information",
      "modules-instantupdate-runner-report-breadcrumb-runner": "Scheduler",
      "modules-instantupdate-runner-report-breadcrumb-report": "Notification Report",
      "modules-instantupdate-runner-new-dialogbox-create-title": "Create New Notification",
      "modules-instantupdate-runner-new-dialogbox-edit-title": "Edit Notification"
    },
    fr: {
      
    },
  },

  // Anamnesis menuKey (reduced Instant Update–style tree)
  "my-local.anamnesis.ai": buildAnamnesisDomainDictionary(),
  "my-stage.anamnesis.ai": buildAnamnesisDomainDictionary(),
  "my.anamnesis.ai": buildAnamnesisDomainDictionary(),

  // Infralytiqs menuKey (reduced Instant Update–style tree)
  "my-local.infralytiqs.com": buildInfralytiqsDomainDictionary(),
  "my-stage.infralytiqs.com": buildInfralytiqsDomainDictionary(),
  "my.infralytiqs.com": buildInfralytiqsDomainDictionary(),
};

const dictionarySlice = createSlice({
  name: 'dictionary',
  initialState,
  reducers: {
    // Replace or add labels for a specific domain+language
    setLabelsForDomainLanguage: (
      state,
      action: PayloadAction<{ domain: string; language: SupportedLanguageCode; labels: DictionaryLabels }>
    ) => {
      const { domain, language, labels } = action.payload;
      if (!state[domain]) {
        state[domain] = {} as DomainDictionary;
      }
      state[domain][language] = labels;
    },
  },
});

export default dictionarySlice.reducer;
export const { setLabelsForDomainLanguage } = dictionarySlice.actions;

// Selectors
export const selectDictionary = (state: any) => state.dictionary as DictionaryState;

/** Aligns with hostname keys in `initialState` (local / stage / prod × CK / IU / Anamnesis / Infralytiqs). */
export type UiDictionaryKey = 'campaignKing' | 'instantUpdate' | 'anamnesis' | 'infralytiqs';

const CANONICAL_DICTIONARY_HOST_BY_TIER: Record<'local' | 'stage' | 'prod', Record<UiDictionaryKey, string>> = {
  local: {
    campaignKing: 'my-local.campaignking.ai',
    instantUpdate: 'my-local.instantupdate.ai',
    anamnesis: 'my-local.anamnesis.ai',
    infralytiqs: 'my-local.infralytiqs.com',
  },
  stage: {
    campaignKing: 'my-stage.campaignking.ai',
    instantUpdate: 'my-stage.instantupdate.ai',
    anamnesis: 'my-stage.anamnesis.ai',
    infralytiqs: 'my-stage.infralytiqs.com',
  },
  prod: {
    campaignKing: 'my.campaignking.ai',
    instantUpdate: 'my.instantupdate.ai',
    anamnesis: 'my.anamnesis.ai',
    infralytiqs: 'my.infralytiqs.com',
  },
};

function resolveDictionaryTier(browserHostname: string): 'local' | 'stage' | 'prod' {
  const h = browserHostname.toLowerCase();
  if (h.startsWith('my-local.') || h === 'localhost' || h.startsWith('127.0.0.1')) return 'local';
  if (h.startsWith('my-stage.')) return 'stage';
  return 'prod';
}

function isUiDictionaryKey(value: string | undefined): value is UiDictionaryKey {
  return value === 'campaignKing' || value === 'instantUpdate' || value === 'anamnesis' || value === 'infralytiqs';
}

function resolveLabelsBucketHostname(
  browserHostname: string,
  uiDictionaryKey: UiDictionaryKey | undefined,
  menuKey: string | undefined,
): string {
  const override = isUiDictionaryKey(uiDictionaryKey) ? uiDictionaryKey : undefined;
  const fromMenu = isUiDictionaryKey(menuKey) ? menuKey : undefined;
  const uiKey = override ?? fromMenu;
  if (isUiDictionaryKey(uiKey)) {
    const tier = resolveDictionaryTier(browserHostname);
    return CANONICAL_DICTIONARY_HOST_BY_TIER[tier][uiKey];
  }
  return browserHostname;
}

export const selectLabels = (
  state: { dictionary: DictionaryState; clientConfig?: { data?: { uiDictionaryKey?: string; menuKey?: string } | null } },
  language: SupportedLanguageCode = 'en',
): DictionaryLabels | undefined => {
  const dict = state.dictionary;
  const browserHost = typeof window !== 'undefined' ? window.location.hostname : 'my.instantupdate.ai';
  const cfg = state.clientConfig?.data;
  const bucketHost = resolveLabelsBucketHostname(
    browserHost,
    cfg?.uiDictionaryKey as UiDictionaryKey | undefined,
    cfg?.menuKey ?? FALLBACK_MENU_KEY,
  );
  return dict[bucketHost]?.[language];
};


